/**
 * Centralized device and orientation detection
 * Provides consistent detection across the entire app
 */

export const DeviceType = {
  DESKTOP: 'desktop',
  TABLET: 'tablet',
  PHONE: 'phone'
}

export const Orientation = {
  PORTRAIT: 'portrait',
  LANDSCAPE: 'landscape'
}

/**
 * Detect device type based on userAgent and viewport
 */
export function getDeviceType() {
  const ua = navigator.userAgent

  // Check for iPad specifically (including iPad Pro)
  if (/iPad/i.test(ua)) {
    return DeviceType.TABLET
  }

  // Check for iPhone and Android phones
  if (/iPhone|iPod/i.test(ua)) {
    return DeviceType.PHONE
  }

  // Check for Android devices
  if (/Android/i.test(ua)) {
    // Android tablets typically have larger screens, but without specific detection,
    // we'll classify based on viewport size as fallback
    const width = Math.max(window.innerWidth, window.innerHeight)
    const height = Math.min(window.innerWidth, window.innerHeight)
    
    // If aspect ratio suggests tablet (closer to 4:3 or wider)
    if (width / height < 2 && width > 600) {
      return DeviceType.TABLET
    }
    return DeviceType.PHONE
  }

  // iPadOS 13+ with "Request Desktop Website" enabled sends a Mac desktop UA
  // (no "iPad" string, platform = "MacIntel"). Detect it via maxTouchPoints:
  // real Macs have maxTouchPoints = 0; iPad touch screens report 5.
  if (/Macintosh/i.test(ua) && navigator.maxTouchPoints > 1) {
    return DeviceType.TABLET
  }

  // Default to desktop for non-mobile userAgents
  return DeviceType.DESKTOP
}

/**
 * Detect current orientation
 */
export function getOrientation() {
  return window.innerHeight > window.innerWidth ? Orientation.PORTRAIT : Orientation.LANDSCAPE
}

// Cache for device info to prevent object creation on every call
let cachedDeviceInfo = null
let cachedOrientation = null
let lastWidth = 0
let lastHeight = 0

/**
 * Combined device and orientation info (cached for performance)
 */
export function getDeviceInfo() {
  const currentWidth = window.innerWidth
  const currentHeight = window.innerHeight
  
  // Only recalculate if dimensions changed (orientation change)
  if (!cachedDeviceInfo || lastWidth !== currentWidth || lastHeight !== currentHeight) {
    const deviceType = getDeviceType()
    const orientation = getOrientation()
    const isMobile = deviceType !== DeviceType.DESKTOP

    cachedDeviceInfo = {
      deviceType,
      orientation,
      isMobile,
      isPhone: deviceType === DeviceType.PHONE,
      isTablet: deviceType === DeviceType.TABLET,
      isDesktop: deviceType === DeviceType.DESKTOP,
      isPortrait: orientation === Orientation.PORTRAIT,
      isLandscape: orientation === Orientation.LANDSCAPE
    }
    
    lastWidth = currentWidth
    lastHeight = currentHeight
  }

  return cachedDeviceInfo
}

/**
 * Get media query key for camera config selection
 * Returns one of: desktop4k, desktop1440, desktop1080, tabletLandscape, tabletPortrait, mobileLandscape, mobilePortrait
 *
 * Note: the old `desktop720` tier (1367–1919) was removed — that range now uses
 * the Tablet Landscape (iPad-landscape) styling, so Tablet Landscape covers 900–1919.
 */
export function getMediaQueryKey() {
  const width = window.innerWidth
  const isPortrait = window.innerHeight > window.innerWidth
  const deviceType = getDeviceType()
  const isPhoneUA = deviceType === DeviceType.PHONE

  // Portrait check — if in portrait mode, viewport width is constrained so
  // desktop breakpoints don't apply. Check portrait cases first when in portrait.
  if (isPortrait) {
    if (width <= 767 || isPhoneUA) return 'mobilePortrait'
    if (width <= 1366) return 'tabletPortrait'
  }

  // Desktop breakpoints (landscape / non-portrait only beyond this point)
  if (width >= 3840) return 'desktop4k'
  if (width >= 2560) return 'desktop1440'   // QHD 1440p — CSS 2560–3839 tier
  if (width >= 1920) return 'desktop1080'   // FHD 1080p — a 1920×1080 monitor lands here

  // Tablet landscape — now covers everything below 1080p (900–1919). The old
  // desktop720 tier (1367–1919) was removed; that range uses iPad-landscape styling.
  if (width >= 900 && width <= 1919) return 'tabletLandscape'

  // Mobile landscape
  if (isPhoneUA || width <= 899) return 'mobileLandscape'

  // Fallback
  return 'tabletLandscape'
}

/**
 * Clear device info cache (call on window resize if needed)
 */
export function clearDeviceInfoCache() {
  cachedDeviceInfo = null
  lastWidth = 0
  lastHeight = 0
}

// Expose globally for memory cleanup
if (typeof window !== 'undefined') {
  window.clearDeviceInfoCache = clearDeviceInfoCache
}
